import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import moment from "moment";
import { AiOutlineDownload } from "react-icons/ai";

import Table from "./Table";
import Loader from "./Loader";
import Input from "./Input";
import { downloadReturnContent, getReturnContentList } from "../redux/authSlice";

const PAGE_SIZE = 10;

const RejectEvidenceListing = () => {
  const dispatch = useDispatch();
  const location = useLocation();

  const [returnContentList, setReturnContentList] = useState([]);
  const [recordCount, setRecordCount] = useState("");
  const [/** pageMaxIndex **/, setPageMaxIndex] = useState<any>("");
  const [pageIndex, setPageIndex] = useState<any>(0);
  const [pageSize, setPageSize] = useState<any>(PAGE_SIZE);
  const [searchQuery, setSearchQuery] = useState("");
  const [appliedSearchQuery, setAppliedSearchQuery] = useState("");
  const [orderByFields, setOrderByFields] = useState("-contentDate");
  const [resetInputField, setResetInputField] = useState<any>(false);
  const [isLoading, setIsLoading] = useState(false);

  const fetchList = async (
    index: any = pageIndex,
    size: any = pageSize,
    query: any = appliedSearchQuery,
    sort: any = orderByFields
  ) => {
    setIsLoading(true);
    await dispatch(
      getReturnContentList({
        pageIndex: index,
        pageSize: size,
        searchQuery: query,
        orderByFields: sort,
        setReturnContentList,
        setRecordCount,
        setReturnContentsPageMaxIndex: setPageMaxIndex,
      })
    );
    setIsLoading(false);
  };

  useEffect(() => {
    fetchList(0, PAGE_SIZE, "", "-contentDate");
    // Refetch on navigation back to this screen, matching the other listings.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  const loadNextProds = (index: any, size: any) => {
    const nextIndex = index > 0 ? index - 1 : 0;
    setResetInputField(false);
    setPageIndex(nextIndex);
    setPageSize(size);
    fetchList(nextIndex, size);
  };

  const onChange = (_pagination: any, _filters: any, sorter: any) => {
    const direction = sorter?.order === "descend" ? "-" : "";
    const nextSort = sorter?.order ? `${direction}${sorter.field}` : "-contentDate";
    setOrderByFields(nextSort);
    fetchList(pageIndex, pageSize, appliedSearchQuery, nextSort);
  };

  const applySearch = (value: string) => {
    setResetInputField(true);
    setAppliedSearchQuery(value);
    setPageIndex(0);
    fetchList(0, pageSize, value);
  };

  const columns = [
    {
      title: "Return ID",
      dataIndex: "returnId",
      key: "returnId",
      sorter: { multiple: 1 },
      render: (returnId: any) => <span className="text-xs">{returnId}</span>,
    },
    {
      title: "Reason",
      dataIndex: "reason",
      key: "reason",
      render: (reason: any) => <span className="text-xs">{reason || "-"}</span>,
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      render: (description: any) => (
        <span className="text-xs">{description || "-"}</span>
      ),
    },
    {
      title: "Product",
      dataIndex: "product",
      key: "product",
      render: (product: any) => <span className="text-xs">{product || "-"}</span>,
    },
    {
      title: "Date",
      dataIndex: "contentDate",
      key: "contentDate",
      sorter: { multiple: 1 },
      render: (contentDate: any) => (
        <span className="text-xs">
          {contentDate ? moment(contentDate).format("YYYY-MM-DD HH:mm") : "-"}
        </span>
      ),
    },
    {
      title: "Evidence",
      dataIndex: "returnContentId",
      key: "returnContentId",
      render: (returnContentId: any, row: any) =>
        returnContentId ? (
          <button
            type="button"
            className="flex flex-row items-center text-xs text-primary underline"
            onClick={() =>
              dispatch(
                downloadReturnContent({
                  returnContentId,
                  fileName: row?.fileName,
                  setIsLoading,
                })
              )
            }
          >
            <AiOutlineDownload className="mr-1" />
            {row?.fileName || "Download"}
          </button>
        ) : (
          <span className="text-xs">-</span>
        ),
    },
  ];

  return (
    <div>
      <Loader isLoading={isLoading}>
        <div className="mb-4 w-full sm:w-1/3">
          <Input
            title=""
            placeholder="Search return, product, or reason"
            value={searchQuery}
            onChange={(event: any) => setSearchQuery(event.target.value)}
            onKeyDown={(event: any) => {
              if (event.key === "Enter") applySearch(searchQuery);
            }}
          />
        </div>

        <Table
          columns={columns}
          dataSource={returnContentList}
          rowKey="returnContentId"
          isPaginated
          onChange={onChange}
          loadNextProds={loadNextProds}
          recordCount={recordCount}
          resetInputField={resetInputField}
          isServerRendered={recordCount ? true : false}
        />
      </Loader>
    </div>
  );
};

export default RejectEvidenceListing;
